import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import AuthCard from '../components/AuthCard';
import Button from '../components/Button';
import Field from '../components/Field';
import FormBanner from '../components/FormBanner';
import { errorMessage } from '../lib/errors';
import {
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH,
  PASSWORD_MAX_LENGTH,
  emailError,
  nameError,
  passwordError,
} from '../lib/validation';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
  }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: { name?: string; email?: string; password?: string } = {};
    const eErr = emailError(email);
    if (eErr) errs.email = eErr;
    const pErr = passwordError(password);
    if (pErr) errs.password = pErr;
    const nErr = nameError(name);
    if (nErr) errs.name = nErr;
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setFormError(null);
    setSubmitting(true);
    try {
      // Registration is enumeration-safe and does NOT create a session: the
      // response is identical whether or not the email already existed, and it
      // always sets the pending-verification cookie. So we always continue to
      // the code screen — for an address that already has a verified account the
      // token is a decoy, which is exactly what keeps the two indistinguishable.
      await api.register({ email: email.trim(), password, name: name.trim() });
      navigate('/verify-email', { replace: true });
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Create your account"
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-[var(--cad-accent,#818cf8)] hover:text-[var(--cad-accent,#a5b4fc)]">
            Sign in
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {formError ? <FormBanner kind="error">{formError}</FormBanner> : null}
        <Field
          label="Name (optional)"
          name="name"
          type="text"
          autoComplete="name"
          maxLength={NAME_MAX_LENGTH}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={fieldErrors.name}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          maxLength={EMAIL_MAX_LENGTH}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          maxLength={PASSWORD_MAX_LENGTH}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
        />
        <Button type="submit" loading={submitting} className="w-full">
          Create account
        </Button>
      </form>
    </AuthCard>
  );
}
